import { apiResourceUrl } from "@ploy-lib/core";
import { ID } from "../types";
import { BaseResource } from "./BaseResource";
import { ApplicationResource } from "./ApplicationResource";
import { EndpointExtraOptions } from "@rest-hooks/rest";

export enum AMLRisk {
	NoContact = "NoContact",
	NotSet = "NotSet",
	Low = "Low",
	Medium = "Medium",
	High = "High"
}

export enum CustomerAMLRole {
	NotSet = "NotSet",
	Customer = "Customer",
	CoSigner = "CoSigner",
	BeneficialOwner = "BeneficialOwner",
	Dagligleder = "Dagligleder",
	Nestleder = "Nestleder",
	Obervatør = "Observatør",
	Styremedlem = "Styremedlem",
	Styretsleder = "Styretsleder",
	Varamedlem = "Varamedlem",
	Kontaktperson = "Kontaktperson",
	Fullmaktshaver = "Fullmaktshaver",
	Unknown = "Unknown"
}

export interface RiskResult {
	name: string;
	result: AMLRisk;
	riskScore: number;
	value?: string;
}

export interface CustomerAMLResult {
	firstName: string;
	lastName: string;
	role: CustomerAMLRole;
	riskResults: RiskResult[];
}

interface PolicyRulesResult {
	id: ID;
	value: number;
}

interface RcOverrideResult {
	id: ID;
	userFullName: string;
	previousTotalRisk: number;
	newTotalRisk: number;
	comment: string;
	timestamp: string;
}

export class ApplicationAMLRisksResource extends BaseResource {
	readonly id?: ID;
	readonly applicationNumber?: string;
	readonly totalRisk: AMLRisk = AMLRisk.NotSet;
	readonly overrideRisk?: AMLRisk;
	readonly customerAMLResults: CustomerAMLResult[] = [];
	readonly riskResults: RiskResult[] = [];
	readonly canOverride: Boolean;
	readonly showRiskScoreValue: Boolean;

	pk() {
		return this.id?.toString();
	}

	// static indexes = ["applicationNumber"];

	static urlTemplates = [
		`${ApplicationResource.urlRoot}/{applicationNumber}/aml-risk`
	];

	static urlRoot = apiResourceUrl("aml-risks");
}

export class ApplicationAMLRiskHistoryResource extends BaseResource {
	readonly id?: ID;
	readonly userFullName?: string;
	readonly risk: AMLRisk = AMLRisk.NotSet;
	readonly comment: string = "";
	readonly timestamp: string = "";

	pk() {
		return this.id?.toString();
	}

	static getEndpointExtra(): EndpointExtraOptions {
		return {
			...super.getEndpointExtra(),
			pollFrequency: 60 * 1000,
			dataExpiryLength: 30 * 1000
		};
	}

	static urlTemplates = ApplicationAMLRisksResource.urlTemplates.map(
		r => `${r}/history`
	);

	static urlRoot = `${ApplicationAMLRisksResource.urlRoot}/{amlRiskId}/history`;
}

export class ApplicationAMLRiskClassificationResultResource extends BaseResource {
	readonly id?: ID;
	readonly value: number;

	pk() {
		return this.id?.toString();
	}

	static urlTemplates = ApplicationAMLRisksResource.urlTemplates.map(
		r => `${r}/policy-rules/result`
	);

	static urlRoot = `${ApplicationAMLRisksResource.urlRoot}/{amlRiskId}/policy-rules/result`;

	static fromJS(data: any): any {
		if (!data || data.id === undefined) {
			const emptyValue: PolicyRulesResult = { id: -1, value: -1 };
			return super.fromJS({ ...new this(), ...emptyValue });
		}
		return super.fromJS(data);
	}
}

export class ApplicationAMLRiskPolicyRulesResource extends BaseResource {
	readonly id?: ID;
	readonly name: string;
	readonly description: string;
	readonly status: string;
	readonly message: string;
	readonly value: number;

	pk() {
		return this.id?.toString();
	}

	static urlTemplates = ApplicationAMLRisksResource.urlTemplates.map(
		r => `${r}/policy-rules`
	);

	static urlRoot = `${ApplicationAMLRisksResource.urlRoot}/{amlRiskId}/policy-rules`;
}

export class ApplicationAMLOverrideResource extends BaseResource {
	readonly id: ID;
	readonly userFullName: string;
	readonly previousTotalRisk: number;
	readonly newTotalRisk: number;
	readonly comment: string;
	readonly timestamp: string;

	pk() {
		return this.id?.toString();
	}

	static urlTemplates = ApplicationAMLRisksResource.urlTemplates.map(
		r => `${r}/rc-override`
	);

	static urlRoot = `${ApplicationAMLRisksResource.urlRoot}/{amlRiskId}/rc-override`;

	static fromJS(data: any): any {
		if (!data || data.id === undefined) {
			const emptyValue: RcOverrideResult = {
				id: -1,
				userFullName: "",
				previousTotalRisk: -1,
				newTotalRisk: -1,
				comment: "",
				timestamp: ""
			};
			return super.fromJS({ ...new this(), ...emptyValue });
		}
		return super.fromJS(data);
	}
}

export class ApplicationAMLOverrideHistoryResource extends BaseResource {
	readonly id: ID;
	readonly userFullName: string;
	readonly previousTotalRisk: number;
	readonly newTotalRisk: number;
	readonly comment: string;
	readonly timestamp: string;

	pk() {
		return this.id?.toString();
	}

	static urlTemplates = ApplicationAMLRisksResource.urlTemplates.map(
		r => `${r}/rc-override/all`
	);

	static urlRoot = `${ApplicationAMLRisksResource.urlRoot}/{amlRiskId}/rc-override/all`;
}
